// The fold law for the world view's agent rails and the shell's context rail
// (CLEO-CAN FD01–FD29 lineage): arrangement is a pure projection of measured
// width, the shared rail width, the person's cap, and hand folds; nothing
// here is stored and the staircase re-derives every render (FD01).
//
// The ALLOCATOR is the donor's — APPS/cockpit/lib/cockpit/fold.ts — imported
// directly, the same way backend/convex/cockpit.ts shares the donor reducer
// and projection: one copy of the width math, never a second (DL4). This
// module keeps only what is this room's own: the chain fold over lineage
// slice ids, its seating steps, and the named deviations — no Pocket door
// yet (the ego holds the floor and the context rib never yields the desk)
// and chain-only re-homing (FD17's first two steps; peers do not exist in
// this room yet). The ego deviation is CLOSED (principal ruling 2026-08-26):
// the lineage's level 0 is Cleo's root session rail — its own width, always open,
// never a spine — riding the donor allocator's own ego slot.

import {
  AGENT_RAIL_PX,
  AGENT_SPINE_PX,
  CONTEXT_RAIL_PX,
  EGO_RAIL_PX,
  egoWishLadder,
  fitsWithSides,
  maxContextFullPx,
  normalizeRailCap,
  RAIL_CAP_DEFAULT,
  RAIL_CAP_MAX,
  RAIL_CAP_MIN,
  SIDE_RIB_PX,
  sideFurnitureLadder,
  DECK_MIN_PX,
} from "../../../../../cockpit/lib/cockpit/fold";

export {
  AGENT_RAIL_PX,
  AGENT_SPINE_PX,
  CONTEXT_RAIL_PX,
  EGO_RAIL_PX,
  // Widen Cleo's step of the shared ladder: the shell's plan for the dock and
  // the Cleo Panel's reserved width when she asks for more than her fixed column.
  egoWishLadder,
  // The drag-law ceiling (the ladder's inverse first step, read from the
  // donor): the widest context column that still holds, so the resize
  // interaction can be bounded by the same formula the fold reads.
  maxContextFullPx,
  normalizeRailCap,
  RAIL_CAP_DEFAULT,
  RAIL_CAP_MAX,
  RAIL_CAP_MIN,
  SIDE_RIB_PX,
  DECK_MIN_PX,
};

/**
 * The physical fit (FD03/FD04) — the donor formula read in the center frame:
 * the context term is zero here (the shell's own furniture already
 * subtracted those pixels before this room received its stage), while the
 * root session rail lives INSIDE this room, so its live width rides the donor ego
 * slot. `totalRails` counts the NESTED rails only — the ego reserves no
 * spine because it never folds. Width may only LOWER the person's cap,
 * never raise it (FD05/FD06); both resizable widths are fold inputs.
 */
export function agentRailFit(input: {
  readonly stageWidth: number;
  readonly agentRailPx: number;
  readonly totalRails: number;
  /** The root session rail's live (person-resizable) width. */
  readonly egoPx: number;
}): number {
  return fitsWithSides(input.stageWidth, input.totalRails, input.egoPx, 0, input.agentRailPx);
}

/**
 * THE ROOM'S NESTING CEILING — the one place to change it (principal ruling
 * 2026-09-02: three panels for now). Counted in PANELS: Cleo's root session rail plus
 * its nested descendants, so 3 means main session → sub-session → one more
 * child. `MAX_NESTED_RAILS` is the same ceiling in the NESTED currency the
 * fold and the cap custody speak. The donor's `RAIL_CAP_MAX` stays the
 * cockpit desk's own number — this room deviates deliberately and never
 * raises above the donor's allocator ceiling.
 */
export const MAX_PRIMITIVE_PANELS = 3;
export const MAX_NESTED_RAILS = Math.min(RAIL_CAP_MAX, MAX_PRIMITIVE_PANELS - 1);

/**
 * The nested cap's own clamp: the donor's person cap floors at 1 because the
 * donor desk has no always-open ego among its agent rails — this room does
 * (the ego closure), so "just Cleo" is a lawful HAND choice, not only a
 * width outcome (principal correction 2026-08-31). Zero is legal here; the
 * ceiling is this room's own `MAX_NESTED_RAILS`.
 */
export function normalizeNestedRailCap(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) {
    return Math.min(MAX_NESTED_RAILS, RAIL_CAP_DEFAULT);
  }
  return Math.min(MAX_NESTED_RAILS, Math.max(0, Math.trunc(value)));
}

export interface AgentRailFoldInput {
  /** The lineage's rail slice ids, root → leaf; level 0 is the root session rail. */
  readonly levelSessionIds: ReadonlyArray<string>;
  /** Measured stage width in px; null = not yet measured → everything opens. */
  readonly stageWidth: number | null;
  /** The shared (resizable) open-rail width of the nested rails. */
  readonly agentRailPx: number;
  /** The root session rail's own (person-resizable) width. */
  readonly egoPx: number;
  /** NESTED rails the person allows open; 0 = just Cleo's root session rail. */
  readonly railCap?: number;
  /** Hand-folded rails (FD11): removed from automatic candidacy. */
  readonly collapsed?: ReadonlyArray<string>;
}

export interface AgentRailFoldResult {
  readonly open: ReadonlySet<string>;
  readonly fit: number;
  readonly budget: number;
  /**
   * Where each folded rail's seat lands (FD17, the two chain steps): the
   * nearest open ancestor, else the first open level below it. The host
   * renders the guest as a labeled chip — a pointer that re-addresses, the
   * same species as a ghost chip (TB26) — and the guest's voice fronts IN
   * PLACE when addressed: the host swaps its displayed face, thread, and
   * composer to the guest (PR20–PR22, witnessed in
   * WorldSessionView.test.tsx). Chain-only re-homing remains the named
   * deviation: FD17's peer steps wait for peers to exist in this room.
   */
  readonly seatedIn: ReadonlyMap<string, string>;
}

/**
 * Which rails stay open: the ego (level 0) always — it is fixed furniture,
 * never a fold candidate, so a stale hand fold naming it is ignored. Among
 * the nested rails the deepest (addressed-most) candidates win, since every
 * rail in this room sits on the addressed lineage (FD10's first sort key is
 * the whole story here). Hand-folded rails fold even when deepest (FD11).
 * Every folded NESTED rail keeps a visible spine — the caller renders it;
 * nothing ever collapses to zero width (FD14). The old "never fewer than
 * one open seat" floor is now held by the ego itself: the nested budget may
 * legally reach zero.
 */
export function foldAgentRails(input: AgentRailFoldInput): AgentRailFoldResult {
  const levels = input.levelSessionIds;
  const ego = levels[0];
  if (ego === undefined) {
    return { open: new Set(), fit: 0, budget: 0, seatedIn: new Map() };
  }
  const nested = levels.slice(1);
  const collapsed = new Set(input.collapsed ?? []);
  const candidates = nested.filter((sessionId) => !collapsed.has(sessionId));
  // Before the first measure the physical fit is unknown, but the person's
  // cap still holds — first paint must already obey the ≤N law so nothing
  // visibly collapses a beat later.
  const fit =
    input.stageWidth === null
      ? candidates.length
      : agentRailFit({
          stageWidth: input.stageWidth,
          agentRailPx: input.agentRailPx,
          totalRails: nested.length,
          egoPx: input.egoPx,
        });
  const budget = Math.max(0, Math.min(normalizeNestedRailCap(input.railCap), fit));
  const open = new Set([ego, ...candidates.slice(candidates.length - budget)]);
  return { open, fit, budget, seatedIn: seatFolded(levels, open) };
}

function seatFolded(
  levels: ReadonlyArray<string>,
  open: ReadonlySet<string>,
): ReadonlyMap<string, string> {
  const seatedIn = new Map<string, string>();
  levels.forEach((sessionId, index) => {
    if (open.has(sessionId)) return;
    // FD17 step 1: the nearest open ancestor on the chain…
    for (let i = index - 1; i >= 0; i--) {
      if (open.has(levels[i]!)) {
        seatedIn.set(sessionId, levels[i]!);
        return;
      }
    }
    // …step 2: else the first open level below it.
    for (let i = index + 1; i < levels.length; i++) {
      if (open.has(levels[i]!)) {
        seatedIn.set(sessionId, levels[i]!);
        return;
      }
    }
  });
  return seatedIn;
}

/**
 * Widen Cleo, the room's half (dated 2026-10-07): the Cleo Panel's width
 * above the column the shell reserves for her is a wish that takes only the
 * deck's free space. Every nested column open now stays open, and the one
 * the context law holds while any nested rail exists stays held, so growing
 * into this width moves neither the room's fold nor the context column.
 * The same number is the drag ceiling: a hand act widens Cleo only into
 * free space. An unmeasured stage grows nothing.
 */
export function rootSessionRailWidth(input: {
  readonly stageWidth: number | null;
  /** The column the shell's plan reserves (the context law's ego term). */
  readonly basePx: number;
  /** The widest she asks for: her preference, or Widen Cleo's width. */
  readonly wishPx: number;
  readonly agentRailPx: number;
  /** NESTED rails on the lineage. */
  readonly totalRails: number;
  /** NESTED rails open at the reserved column. */
  readonly openRails: number;
}): number {
  const base = Math.floor(input.basePx);
  const wish = Math.max(base, Math.floor(input.wishPx));
  if (input.stageWidth === null || wish === base) return base;
  const rails = Math.max(0, Math.trunc(input.totalRails));
  const held = Math.max(Math.trunc(input.openRails), rails > 0 ? 1 : 0);
  const openDelta = Math.max(1, Math.floor(input.agentRailPx) - AGENT_SPINE_PX);
  const free =
    Math.floor(input.stageWidth) - base - DECK_MIN_PX - rails * AGENT_SPINE_PX - held * openDelta;
  return Math.max(base, Math.min(wish, base + free));
}

export interface ContextOpenInput {
  /** The whole furniture row: dock + Cleo + nested rails + deck + context. */
  readonly width: number;
  /** NESTED rails, each at least a spine; one open column is held while any exist. */
  readonly totalRails: number;
  readonly agentRailPx: number;
  /** Cleo's reserved column, and the narrowest she may be asked to stand at. */
  readonly ego: { readonly basePx: number; readonly minPx: number };
  /** The context rail's minimum usable column. */
  readonly contextMinPx: number;
  /** The dock's preferred and tight widths; null when the row has none. */
  readonly dock: { readonly fullPx: number; readonly tightPx: number } | null;
}

export interface ContextOpenFurniture {
  /** Whether the context column can stand at its minimum at this step. */
  readonly fits: boolean;
  readonly egoPx: number;
  readonly dockPx: number;
  readonly dockTight: boolean;
  /** The widest the dock may be dragged while this step stands; unbounded when it has not yielded. */
  readonly dockMaxPx: number;
  /** The widest context column the step holds (the context law's own reading). */
  readonly contextPx: number;
}

/**
 * Opening the folded Context rail (dated 2026-10-07, Widen Cleo's principle:
 * one deliberate act may fold other furniture, and no stored preference is
 * rewritten). The same desk ladder read from the other side: the dock
 * renders tight first, then Cleo's reserved column narrows toward her
 * minimum, only as far as the context's minimum column needs, and the deck
 * minimum is never spent. When even that cannot stand, nothing yields and
 * the rail stays a rib: no act may manufacture pixels the window lacks. The
 * context column is read with the context law's own inverse
 * (maxContextFullPx), so the plan and the rail can never disagree. Nothing
 * here is stored: ending the open re-derives the ordinary ladder.
 */
export function contextOpenLadder(input: ContextOpenInput): ContextOpenFurniture {
  const w = Math.floor(input.width);
  const min = Math.floor(input.contextMinPx);
  const base = Math.max(0, Math.floor(input.ego.basePx));
  const egoMin = Math.min(base, Math.floor(input.ego.minPx));
  const dockFull = input.dock === null ? 0 : Math.floor(input.dock.fullPx);
  const dockTight = input.dock === null ? 0 : Math.min(dockFull, Math.floor(input.dock.tightPx));
  const held = (dockPx: number, egoPx: number): number =>
    maxContextFullPx(w - dockPx, input.totalRails, egoPx, input.agentRailPx);
  const at = (egoPx: number, dockPx: number, fits: boolean): ContextOpenFurniture => ({
    fits,
    egoPx,
    dockPx,
    dockTight: dockPx !== dockFull,
    dockMaxPx: dockPx !== dockFull ? dockPx : Number.POSITIVE_INFINITY,
    contextPx: held(dockPx, egoPx),
  });
  if (held(dockFull, base) >= min) return at(base, dockFull, true);
  if (held(dockTight, base) >= min) return at(base, dockTight, true);
  // The column is linear in Cleo's width: she gives exactly the shortfall.
  const egoPx = base - (min - held(dockTight, base));
  if (egoPx >= egoMin) return at(egoPx, dockTight, true);
  return at(base, dockFull, false);
}

export interface ContextRailFurniture {
  readonly contextPx: number;
  readonly folded: boolean;
}

/**
 * The context rail joins the fold ladder (FD24's context-first step). Width
 * is a law, not a style: below the law the rail keeps a SIDE_RIB_PX rib,
 * never `hidden` (FD14's spirit carried to side furniture). The seated
 * room's root session rail rides the ladder's ego slot as fixed, UNFOLDABLE
 * furniture (rib = full width — the principal's ruling: Cleo's rail never
 * folds), so the context yields exactly one ego-width sooner; a roomless
 * shell passes 0 and the ego slot stays absent. `totalRails` counts the
 * NESTED rails only, matching the room's own fit. An unmeasured row renders
 * full — first paint matches the wide door and the first measure settles
 * it, the same rule the agent fold uses. With zero nested rails the ladder
 * protects the ego plus the canvasRevision minimum. Pocket-less deviation: when
 * even the rib cannot hold the law, the rib stays — the rail never
 * vanishes.
 */
export function contextRailFurniture(input: {
  readonly rowWidth: number | null;
  /** NESTED rails on the addressed lineage — the ego reserves no spine. */
  readonly totalRails: number;
  /** The rail's live (person-resizable) full width. */
  readonly contextRailPx: number;
  /** The committed shared agent-rail width — the same fold input the room uses. */
  readonly agentRailPx: number;
  /** The seated root session rail's live width; 0 while no room is seated. */
  readonly rootSessionRailPx: number;
  /**
   * DATED REVISION 2026-09-02 (RL-CAN-39): the rail's own minimum column.
   * A stage that cannot hold the person's full preference but CAN hold a
   * column at least this wide renders the widest column the ladder holds
   * (the donor's maxContextFullPx — the same bound WS7 gives every hand
   * act) and leaves the stored preference untouched (DL4: computed, never
   * rewritten); the rib is reached only when even this minimum cannot
   * stand. Absent = the pre-revision law (fold at the first missing pixel).
   */
  readonly minContextRailPx?: number | undefined;
}): ContextRailFurniture {
  // RL-CAN-14: the person's explicit 26px preference is the same physical
  // rib the automatic ladder uses. It wins even before stage measurement;
  // automatic folds still leave a full-width preference untouched.
  if (input.contextRailPx === SIDE_RIB_PX) {
    return { contextPx: SIDE_RIB_PX, folded: true };
  }
  if (input.rowWidth === null) {
    return { contextPx: input.contextRailPx, folded: false };
  }
  if (input.minContextRailPx !== undefined) {
    const held = maxContextFullPx(
      input.rowWidth,
      input.totalRails,
      input.rootSessionRailPx,
      input.agentRailPx,
    );
    if (held >= input.minContextRailPx) {
      return { contextPx: Math.min(input.contextRailPx, held), folded: false };
    }
  }
  const step = sideFurnitureLadder(
    input.rowWidth,
    input.totalRails,
    {
      ego:
        input.rootSessionRailPx > 0
          ? { fullPx: input.rootSessionRailPx, ribPx: input.rootSessionRailPx } // present, never folds
          : null,
      context: { fullPx: input.contextRailPx, ribPx: SIDE_RIB_PX },
    },
    input.agentRailPx,
  );
  return { contextPx: step.contextPx, folded: step.contextFolded };
}
